import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import AICheckerResult from 'notebook/ai-tools/AICheckerResult';
import {
  CheckEntryResponseState,
  CheckEntryResultUsefulState,
  DisplayCheckEntryResults,
} from 'notebook/ai-tools/NotebookAIToolsConstants';
import {GREEN_DARK, RED} from 'ui-library/Colors';
import BenchlingDuotoneIcon from 'ui-library/icons/BenchlingDuotoneIcon';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface AICheckerResultsProps {
  checkEntryResults: DisplayCheckEntryResults;
  onToggleCompleted: (index: number) => void;
  onToggleUsefulState: (index: number, value: CheckEntryResultUsefulState) => void;
  onRecompute: () => void;
}

/**
 * "Checklist" of results from the AI checker, or information about loading/error state.
 */
export default function AICheckerResults({
  checkEntryResults,
  onToggleCompleted,
  onToggleUsefulState,
  onRecompute,
}: AICheckerResultsProps): JSX.Element {
  if (checkEntryResults.state === CheckEntryResponseState.LOADING) {
    return (
      <div
        className={css(styles.loadingMessage)}
        data-test-component="AICheckerResults"
        data-test-element="loading"
      >
        <BenchlingDuotoneIcon
          icon="spinner-third"
          primaryColor={Color.PURPLE_600}
          secondaryColor={Color.PURPLE_300}
          spin={true}
        />
        <span
          className={css(styles.loadingShimmer)}
          // These need to be defined inline to avoid Aphrodite slapping a !important on this
          // and thus ruining the animation
          style={{
            background: `linear-gradient(120deg, ${Color.GRAY_900} 0%, ${Color.GRAY_500} 20%, ${Color.GRAY_200} 40%, ${Color.GRAY_500} 60%, ${Color.GRAY_900} 80%) 0 0 / 200% 100%`,
            backgroundClip: 'text',
            backgroundSize: '200% 100%',
            WebkitBackgroundClip: 'text',
          }}
        >
          {checkEntryResults.loadingState}
        </span>
      </div>
    );
  } else if (checkEntryResults.state === CheckEntryResponseState.ERROR) {
    return (
      <div className="_test-error-message">
        <span className={css(styles.errorIcon)}>
          <BenchlingIcon icon="error" />
        </span>
        {checkEntryResults.errorMessage ?? 'An error occurred when checking the entry.'}{' '}
        <a onClick={onRecompute}>Retry</a>
      </div>
    );
  } else if (checkEntryResults.results.length === 0) {
    return (
      <div data-test-component="AICheckerResults" data-test-element="root">
        <span className={css(styles.passedIcon)}>
          <BenchlingIcon icon="check" />
        </span>
        All checks passed.
      </div>
    );
  }

  return (
    <div data-test-component="AICheckerResults" data-test-element="root">
      {checkEntryResults.results.map((result, index) => (
        <AICheckerResult
          key={`${checkEntryResults.traceId}:${index}`}
          result={result}
          onToggleCompleted={() => {
            onToggleCompleted(index);
          }}
          onToggleUseful={() => {
            onToggleUsefulState(index, CheckEntryResultUsefulState.USEFUL);
          }}
          onToggleNotUseful={() => {
            onToggleUsefulState(index, CheckEntryResultUsefulState.NOT_USEFUL);
          }}
        />
      ))}
    </div>
  );
}

const keyframes = {
  '0%': {
    backgroundPosition: '200% 0',
  },
  '100%': {
    backgroundPosition: '-200% 0',
  },
};

const styles = StyleSheet.create({
  loadingMessage: {
    alignItems: 'center',
    display: 'flex',
    fontSize: 14,
    gap: 8,
  },
  loadingShimmer: {
    animationName: [keyframes],
    animationDelay: '0s',
    animationDuration: '3s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'linear',
    color: 'transparent',
    display: 'inline-block',
    WebkitTextFillColor: 'transparent',
  },
  passedIcon: {
    color: GREEN_DARK,
    marginRight: 4,
  },
  errorIcon: {
    color: RED,
    marginRight: 4,
  },
});
